import { RmsAccountSelection } from '@/ui/features/badging/badging-customizations/badge-create/types';
import { AccountBadgeOptionsSelectProps } from '@juvare/badging/ui/@types/AccountBadgeOptionsSelectProps';
import { OccupationColumns, occupationEntityName } from '@juvare/credentialing/backend/config/shared';
import { useStorageList } from '@juvare/platform-sdk-react/ui/hooks';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import Typography from '@mui/material/Typography';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { SWRConfiguration } from 'swr';

export const RmsAccountOccupationSelect: React.FC<AccountBadgeOptionsSelectProps<RmsAccountSelection>> = ({
    accountSelection,
    onOptionsPartialChange,
    hideDropdownWhenNoOptions,
    hideDropdownWhenOneOption,
    sx,
}) => {
    const rmsAccountSelection = accountSelection as RmsAccountSelection;

    const { t } = useTranslation('rms');

    const { data, isLoading } = useStorageList<OccupationColumns>(occupationEntityName, {}, undefined, undefined, {
        dedupingInterval: 30000, // avoid unnecessary re-fetching
    } satisfies SWRConfiguration);

    const occupations = data?.rows ?? [];
    const accountOccupations = occupations.filter((occupation) => rmsAccountSelection.account.occupationIds.includes(occupation.id));
    const sxWidth = (sx as { width?: string | number } | undefined)?.width;
    const selectedOccupation = accountOccupations.find((occupation) => occupation.id === rmsAccountSelection.options.occupationId);

    React.useEffect(() => {
        if (
            rmsAccountSelection.options.occupationId &&
            accountOccupations.every((o) => o.id !== rmsAccountSelection.options.occupationId)
        ) {
            // invalid occupation is pre-selected - unselect
            onOptionsPartialChange({
                ...rmsAccountSelection.options,
                occupationId: undefined,
            });
        }
    }, [rmsAccountSelection.options, accountOccupations, onOptionsPartialChange]);

    if (isLoading) {
        return <CircularProgress />;
    }
    if (accountOccupations.length === 0 && hideDropdownWhenNoOptions) {
        return (
            <Typography
                variant='body1'
                sx={{
                    textAlign: 'center',
                    width: sxWidth ?? '100%',
                }}
            >
                -
            </Typography>
        );
    }
    if (
        accountOccupations.length === 1 &&
        accountOccupations[0].id === rmsAccountSelection.options.occupationId &&
        hideDropdownWhenOneOption
    ) {
        return (
            <Typography
                variant='body1'
                sx={{
                    textAlign: 'center',
                    width: sxWidth ?? '100%',
                }}
            >
                {accountOccupations[0].columns.name}
            </Typography>
        );
    }

    return (
        <Autocomplete
            sx={sx}
            size='small'
            value={selectedOccupation ?? null}
            autoComplete={true}
            onChange={(_event, option) => onOptionsPartialChange({ ...rmsAccountSelection.options, occupationId: option?.id })}
            disabled={
                accountOccupations.length == 0 ||
                (accountOccupations.length === 1 && accountOccupations[0].id == rmsAccountSelection.options.occupationId)
            }
            options={accountOccupations}
            renderInput={(params) => <TextField {...params} label={t('badging.accountBadgeTable.selectOccupation')} />}
            getOptionLabel={(option) => option.columns.name}
            disableClearable={!!selectedOccupation}
            loading={isLoading}
        />
    );
};
